/**
 * Permission Service - Supabase CRUD Operations
 *
 * Handles all permission-related database operations.
 * Works with default_permissions and academy_permissions tables.
 *
 * @module permissionService
 */

import { supabase, isSupabaseConfigured } from "~/shared/lib/supabase";
import { queryWithAuthRetry, mutateWithAuthRetry } from "~/core/api/executeWithAuthRetry";
import type { UserRole } from "~/types/database";
import { SyncError } from "~/core/sync/SyncError";

// ============================================================================
// TYPES
// ============================================================================

/** Permission item from database */
export interface PermissionItem {
  permission: string;
  granted: boolean;
  isCustom: boolean;
}

/** Permission matrix for UI (role -> permission -> granted) */
export type PermissionMatrix = Record<string, Record<string, boolean>>;

/** Preset definition */
export interface PermissionPreset {
  id: string;
  name: string;
  description: string | null;
  permissions: Record<string, Record<string, boolean>>;
  isSystem: boolean;
}

/** Permission category for UI grouping */
export interface PermissionCategory {
  id: string;
  label: string;
  permissions: PermissionDef[];
}

/** Permission definition for UI */
export interface PermissionDef {
  id: string;
  label: string;
  description?: string;
}

// ============================================================================
// PERMISSION DEFINITIONS (for UI)
// ============================================================================

/**
 * All permission definitions grouped by category
 * Used for rendering the permission matrix UI
 */
export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: "session",
    label: "수업",
    permissions: [
      { id: "session.create", label: "수업 생성", description: "새 수업을 생성할 수 있습니다" },
      { id: "session.view", label: "수업 조회", description: "수업 일정을 볼 수 있습니다" },
      { id: "session.edit_own", label: "내 수업 수정", description: "자신의 수업을 수정할 수 있습니다" },
      { id: "session.edit_all", label: "모든 수업 수정", description: "다른 선생님의 수업도 수정할 수 있습니다" },
      { id: "session.delete_own", label: "내 수업 삭제", description: "자신의 수업을 삭제할 수 있습니다" },
      { id: "session.delete_all", label: "모든 수업 삭제", description: "다른 선생님의 수업도 삭제할 수 있습니다" },
      { id: "session.approve", label: "수업 승인", description: "수업 요청을 승인/거절할 수 있습니다" },
    ],
  },
  {
    id: "student",
    label: "학생",
    permissions: [
      { id: "student.create", label: "학생 추가", description: "새 학생을 등록할 수 있습니다" },
      { id: "student.view", label: "학생 조회", description: "학생 정보를 볼 수 있습니다" },
      { id: "student.edit", label: "학생 수정", description: "학생 정보를 수정할 수 있습니다" },
      { id: "student.delete", label: "학생 삭제", description: "학생을 삭제할 수 있습니다" },
    ],
  },
  {
    id: "teacher",
    label: "선생님",
    permissions: [
      { id: "teacher.view", label: "선생님 조회", description: "선생님 목록을 볼 수 있습니다" },
      { id: "teacher.invite", label: "선생님 초대", description: "새 선생님을 초대할 수 있습니다" },
      { id: "teacher.edit", label: "선생님 수정", description: "선생님 정보를 수정할 수 있습니다" },
      { id: "teacher.delete", label: "선생님 삭제", description: "선생님을 삭제할 수 있습니다" },
    ],
  },
  {
    id: "course",
    label: "코스",
    permissions: [
      { id: "course.create", label: "코스 생성", description: "새 코스를 생성할 수 있습니다" },
      { id: "course.view", label: "코스 조회", description: "코스 목록을 볼 수 있습니다" },
      { id: "course.edit", label: "코스 수정", description: "코스 정보를 수정할 수 있습니다" },
      { id: "course.delete", label: "코스 삭제", description: "코스를 삭제할 수 있습니다" },
    ],
  },
  {
    id: "log",
    label: "수업 일지",
    permissions: [
      { id: "log.create", label: "일지 작성", description: "수업 일지를 작성할 수 있습니다" },
      { id: "log.view_own", label: "내 일지 조회", description: "자신의 일지를 볼 수 있습니다" },
      { id: "log.view_all", label: "모든 일지 조회", description: "다른 선생님의 일지도 볼 수 있습니다" },
      { id: "log.edit_own", label: "내 일지 수정", description: "자신의 일지를 수정할 수 있습니다" },
      { id: "log.delete_own", label: "내 일지 삭제", description: "자신의 일지를 삭제할 수 있습니다" },
    ],
  },
  {
    id: "materials",
    label: "자료",
    permissions: [
      { id: "materials.restore", label: "자료 복구", description: "삭제된 학교 자료를 복구할 수 있습니다 (삭제·수정은 누구나 가능, 전부 기록됨)" },
    ],
  },
  {
    id: "report",
    label: "리포트",
    permissions: [
      { id: "report.view", label: "리포트 조회", description: "통계 및 리포트를 볼 수 있습니다" },
      { id: "report.export", label: "데이터 내보내기", description: "데이터를 CSV/Excel로 내보낼 수 있습니다" },
    ],
  },
  {
    id: "settings",
    label: "설정",
    permissions: [
      { id: "settings.view", label: "설정 조회", description: "학원 설정을 볼 수 있습니다" },
      { id: "settings.manage_academy", label: "학원 관리", description: "학원 정보를 수정할 수 있습니다" },
      { id: "settings.manage_permissions", label: "권한 관리", description: "역할별 권한을 설정할 수 있습니다" },
    ],
  },
];

/**
 * All permission IDs as a flat array
 */
export const ALL_PERMISSIONS = PERMISSION_CATEGORIES.flatMap(cat =>
  cat.permissions.map(p => p.id)
);

/**
 * Editable roles (OWNER is always full permissions, not editable)
 */
export const EDITABLE_ROLES: UserRole[] = ["ADMIN", "MANAGER", "TEACHER"];

/**
 * All roles including OWNER
 */
export const ALL_ROLES: UserRole[] = ["OWNER", "ADMIN", "MANAGER", "TEACHER"];

/**
 * Default role display names
 */
export const DEFAULT_ROLE_LABELS: Record<UserRole, string> = {
  OWNER: "대표",
  ADMIN: "관리자",
  MANAGER: "매니저",
  TEACHER: "선생님",
};

/** @deprecated Use DEFAULT_ROLE_LABELS + getRoleLabel() instead */
export const ROLE_LABELS = DEFAULT_ROLE_LABELS;

/**
 * Get role label with custom override support
 */
export function getRoleLabel(
  role: UserRole,
  customLabels: Partial<Record<string, string>> | null | undefined,
): string {
  const custom = customLabels?.[role];
  return (custom && custom.trim() !== "") ? custom : DEFAULT_ROLE_LABELS[role];
}

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Fetch default permissions for a specific role
 */
export async function fetchDefaultPermissions(
  role: UserRole
): Promise<{ success: boolean; data?: PermissionItem[]; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  try {
    // ISSUE_047: Use queryWithAuthRetry for automatic token refresh on 401
    const { data, error } = await queryWithAuthRetry(() =>
      supabase!
        .from("default_permissions")
        .select("permission, granted")
        .eq("role", role)
        .order("permission")
    );

    if (error) {
      console.error("[permissionService] fetchDefaultPermissions error:", error);
      return { success: false, error: error.message };
    }

    const items: PermissionItem[] = (data || []).map(row => ({
      permission: row.permission,
      granted: row.granted,
      isCustom: false,
    }));

    return { success: true, data: items };
  } catch (err) {
    console.error("[permissionService] fetchDefaultPermissions exception:", err);
    return { success: false, error: String(err) };
  }
}

/**
 * Fetch academy permissions (merged with defaults)
 */
export async function fetchAcademyPermissions(
  academyId: string,
  role: UserRole
): Promise<{ success: boolean; data?: PermissionItem[]; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  try {
    // ISSUE_047: Use queryWithAuthRetry for automatic token refresh on 401
    // Use the database function that merges defaults with customizations
    const { data, error } = await queryWithAuthRetry(() =>
      supabase!.rpc("get_academy_permissions", {
        p_academy_id: academyId,
        p_role: role,
      })
    );

    if (error) {
      console.error("[permissionService] fetchAcademyPermissions error:", error);
      return { success: false, error: error.message };
    }

    const items: PermissionItem[] = (data || []).map((row: { permission: string; granted: boolean; is_custom: boolean }) => ({
      permission: row.permission,
      granted: row.granted,
      isCustom: row.is_custom,
    }));

    return { success: true, data: items };
  } catch (err) {
    console.error("[permissionService] fetchAcademyPermissions exception:", err);
    return { success: false, error: String(err) };
  }
}

/**
 * Fetch full permission matrix for an academy (all roles)
 */
export async function fetchPermissionMatrix(
  academyId: string
): Promise<{ success: boolean; data?: PermissionMatrix; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const matrix: PermissionMatrix = {};

    // Fetch for each role in parallel
    const results = await Promise.all(
      ALL_ROLES.map(async role => {
        const result = await fetchAcademyPermissions(academyId, role);
        return { role, result };
      })
    );

    for (const { role, result } of results) {
      if (!result.success || !result.data) {
        return { success: false, error: result.error || "Failed to fetch permissions" };
      }

      matrix[role] = {};
      for (const item of result.data) {
        matrix[role][item.permission] = item.granted;
      }
    }

    return { success: true, data: matrix };
  } catch (err) {
    console.error("[permissionService] fetchPermissionMatrix exception:", err);
    return { success: false, error: String(err) };
  }
}

/**
 * Save academy permissions — Supabase upsert (서버 권위).
 * ISSUE_167: Dexie 큐 제거. pushPermissionMutation(update/academy) 로직 인라인.
 * 낙관 갱신은 소비처(PermissionSettings)가 담당.
 */
export async function saveAcademyPermissions(
  academyId: string,
  role: UserRole,
  permissions: Record<string, boolean>,
  updatedBy?: string
): Promise<{ success: boolean; error?: string }> {
  // OWNER permissions cannot be modified
  if (role === "OWNER") {
    return { success: false, error: "Cannot modify OWNER permissions" };
  }

  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  try {
    type RoleEnum = "OWNER" | "ADMIN" | "MANAGER" | "TEACHER";
    const rows = Object.entries(permissions).map(([permission, granted]) => ({
      academy_id: academyId,
      role: role as RoleEnum,
      permission,
      granted,
      updated_at: new Date().toISOString(),
      updated_by: updatedBy || null,
    }));
    const { error } = await mutateWithAuthRetry(() =>
      supabase!.from("academy_permissions").upsert(rows, {
        onConflict: "academy_id,role,permission",
      })
    );
    if (error) throw SyncError.fromSupabase(error);

    return { success: true };
  } catch (err) {
    console.error("[permissionService] saveAcademyPermissions exception:", err);
    return { success: false, error: String(err) };
  }
}

/**
 * Reset academy permissions to defaults — Supabase delete (서버 권위).
 * ISSUE_167: Dexie 큐 제거. pushPermissionMutation(delete/academy) 로직 인라인.
 */
export async function resetAcademyPermissions(
  academyId: string,
  role?: UserRole
): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  try {
    let query = supabase!
      .from("academy_permissions")
      .delete()
      .eq("academy_id", academyId);
    if (role) {
      query = query.eq("role", role as "OWNER" | "ADMIN" | "MANAGER" | "TEACHER");
    }
    const { error } = await mutateWithAuthRetry(() => query);
    if (error) throw SyncError.fromSupabase(error);

    return { success: true };
  } catch (err) {
    console.error("[permissionService] resetAcademyPermissions exception:", err);
    return { success: false, error: String(err) };
  }
}

/**
 * Fetch available presets
 */
export async function fetchPresets(): Promise<{
  success: boolean;
  data?: PermissionPreset[];
  error?: string;
}> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  try {
    // ISSUE_047: Use queryWithAuthRetry for automatic token refresh on 401
    const { data, error } = await queryWithAuthRetry(() =>
      supabase!
        .from("permission_presets")
        .select("id, name, description, permissions, is_system")
        .order("id")
    );

    if (error) {
      console.error("[permissionService] fetchPresets error:", error);
      return { success: false, error: error.message };
    }

    const presets: PermissionPreset[] = (data || []).map(row => ({
      id: row.id,
      name: row.name,
      description: row.description,
      permissions: row.permissions as Record<string, Record<string, boolean>>,
      isSystem: row.is_system ?? false,
    }));

    return { success: true, data: presets };
  } catch (err) {
    console.error("[permissionService] fetchPresets exception:", err);
    return { success: false, error: String(err) };
  }
}

/**
 * Apply a preset to an academy
 */
export async function applyPreset(
  academyId: string,
  presetId: string,
  updatedBy?: string
): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  try {
    // ISSUE_047: Use queryWithAuthRetry for automatic token refresh on 401
    // Fetch preset
    const { data: preset, error: fetchError } = await queryWithAuthRetry<{ permissions: unknown }>(() =>
      supabase!.from("permission_presets").select("permissions").eq("id", presetId).single()
    );

    if (fetchError || !preset) {
      return { success: false, error: fetchError?.message || "Preset not found" };
    }

    const presetPermissions = preset.permissions as Record<string, Record<string, boolean>>;

    // "default" preset means reset to defaults (delete all customizations)
    if (presetId === "default") {
      return resetAcademyPermissions(academyId);
    }

    // Apply preset permissions for each role
    for (const [role, permissions] of Object.entries(presetPermissions)) {
      if (role === "OWNER") continue; // Skip OWNER

      const result = await saveAcademyPermissions(
        academyId,
        role as UserRole,
        permissions,
        updatedBy
      );

      if (!result.success) {
        return result;
      }
    }

    return { success: true };
  } catch (err) {
    console.error("[permissionService] applyPreset exception:", err);
    return { success: false, error: String(err) };
  }
}

/**
 * Check if user has a specific permission in an academy (client-side cache check)
 * Note: This is for UI purposes. Actual enforcement is done via RLS.
 */
export async function checkPermission(
  academyId: string,
  permission: string
): Promise<boolean> {
  if (!isSupabaseConfigured() || !supabase) {
    return false;
  }

  try {
    // ISSUE_047: Use queryWithAuthRetry for automatic token refresh on 401
    const { data, error } = await queryWithAuthRetry(() =>
      supabase!.rpc("has_permission", {
        p_academy_id: academyId,
        p_permission: permission,
      })
    );

    if (error) {
      return false;
    }

    return data === true;
  } catch {
    return false;
  }
}

/**
 * Batch check multiple permissions
 */
export async function checkPermissions(
  academyId: string,
  permissions: string[]
): Promise<Record<string, boolean>> {
  const results: Record<string, boolean> = {};

  // Check each permission in parallel
  await Promise.all(
    permissions.map(async permission => {
      results[permission] = await checkPermission(academyId, permission);
    })
  );

  return results;
}

// ============================================================================
// TEACHER-LEVEL PERMISSIONS (개인 권한 오버라이드)
// ============================================================================

/** Teacher permission item from database */
export interface TeacherPermissionItem {
  permission: string;
  granted: boolean;
  isPersonalOverride: boolean;
  roleDefault: boolean;
}

/**
 * Fetch teacher's personal permissions
 * Returns merged view: personal override + role default
 */
export async function fetchTeacherPermissions(
  academyId: string,
  teacherId: string
): Promise<{ success: boolean; data?: TeacherPermissionItem[]; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  try {
    // Note: teacher_permissions table and RPC are created by migration 20260221000000
    // Type will be available after regenerating Supabase types
    const { data, error } = await queryWithAuthRetry(() =>
      supabase!.rpc("get_teacher_permissions", {
        p_academy_id: academyId,
        p_teacher_id: teacherId,
      })
    );

    if (error) {
      console.error("[permissionService] fetchTeacherPermissions error:", error);
      return { success: false, error: error.message };
    }

    const items: TeacherPermissionItem[] = (data || []).map((row) => ({
      permission: row.permission,
      granted: row.granted,
      isPersonalOverride: row.is_personal_override,
      roleDefault: row.role_default,
    }));

    return { success: true, data: items };
  } catch (err) {
    console.error("[permissionService] fetchTeacherPermissions exception:", err);
    return { success: false, error: String(err) };
  }
}

/**
 * Save teacher's personal permission overrides — Supabase upsert (서버 권위).
 * ISSUE_167: Dexie 큐 제거. pushPermissionMutation(update/teacher) 로직 인라인.
 */
export async function saveTeacherPermissions(
  academyId: string,
  teacherId: string,
  permissions: Record<string, boolean>,
  updatedBy?: string
): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const rows = Object.entries(permissions).map(([permission, granted]) => ({
      academy_id: academyId,
      teacher_id: teacherId,
      permission,
      granted,
      updated_at: new Date().toISOString(),
      updated_by: updatedBy || null,
    }));
    const { error } = await mutateWithAuthRetry(() =>
      supabase!.from("teacher_permissions").upsert(rows, {
        onConflict: "academy_id,teacher_id,permission",
      })
    );
    if (error) throw SyncError.fromSupabase(error);

    return { success: true };
  } catch (err) {
    console.error("[permissionService] saveTeacherPermissions exception:", err);
    return { success: false, error: String(err) };
  }
}

/**
 * Reset teacher's personal permissions to role defaults — Supabase RPC (서버 권위).
 * ISSUE_167: Dexie 큐 제거. pushPermissionMutation(delete/teacher) 로직 인라인.
 */
export async function resetTeacherPermissions(
  academyId: string,
  teacherId: string
): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const { error } = await mutateWithAuthRetry(() =>
      supabase!.rpc("reset_teacher_permissions", {
        p_academy_id: academyId,
        p_teacher_id: teacherId,
      })
    );
    if (error) throw SyncError.fromSupabase(error);

    return { success: true };
  } catch (err) {
    console.error("[permissionService] resetTeacherPermissions exception:", err);
    return { success: false, error: String(err) };
  }
}
